<template>
  <div class="tw-w-full verify-details tw-flex tw-flex-col tw-items-center tw-mt-8 tw-px-4 sm:tw-px-6 lg:tw-px-12">
    <div class="tw-w-full tw-max-w-screen-xl">
      <h2 style="color: #114cac;" class="tw-text-2xl md:tw-text-3xl tw-font-semibold tw-mb-4 tw-text-center md:tw-text-left">
        Verify Details
      </h2>

      <!-- Quote Information Section -->
      <div class="tw-bg-blue-100 tw-text-blue-700 tw-font-semibold tw-rounded-md tw-flex tw-flex-col sm:tw-flex-row tw-py-4 tw-px-4 lg:tw-px-6 tw-mb-6">
        <!-- Cover Type -->
        <div class="tw-w-full sm:tw-w-1/3 tw-text-center sm:tw-text-left tw-mb-2 sm:tw-mb-0">
          <p class="tw-font-semibold tw-mb-1">Cover Type</p>
          <p class="tw-font-bold">{{ coverType }}</p>
        </div>

        <!-- Quote Period -->
        <div class="tw-w-full sm:tw-w-1/3 tw-text-center tw-mb-2 sm:tw-mb-0">
          <p class="tw-font-semibold tw-mb-1">Quote Period</p>
          <p class="tw-font-normal">From {{ formattedQuotePeriod }}</p>
        </div>

        <!-- Quote Number -->
        <div class="tw-w-full sm:tw-w-1/3 tw-text-center sm:tw-text-right">
          <p class="tw-font-semibold tw-mb-1">Quote Number</p>
          <p class="tw-font-normal">{{ quoteNumber }}</p>
        </div>
      </div>

      <hr class="tw-border-gray-300 tw-my-4">

      <!-- Principal Member Details -->
      <div class="tw-mb-6">
        <h3 class="tw-font-semibold tw-text-lg md:tw-text-xl tw-mb-2 tw-text-center md:tw-text-left" style="color: #114cac;">
          Principal Member Details
        </h3>
        <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-y-4 tw-gap-x-8 tw-text-gray-700">
          <div
              v-for="(value, key) in principalDetails"
              :key="key"
              class="tw-flex tw-items-start tw-space-x-2"
          >
            <span class="tw-font-semibold tw-w-1/2 tw-text-left ">
              <template v-if="key=='kra'">KRA Pin No.</template>
              <template v-else-if="key=='idNumber'">ID No.</template>
              <template v-else> {{ key }}</template>
            </span>
            <span class="tw-text-gray-600 tw-w-1/2 tw-text-left">{{ value }}</span>
          </div>
        </div>
      </div>

      <hr class="tw-border-gray-300 tw-my-4">

      <!-- Vehicle Details -->
      <div class="tw-mb-6">
        <h3 class="tw-font-semibold tw-text-lg md:tw-text-xl tw-mb-2 tw-text-center md:tw-text-left" style="color: #114cac;">
          Vehicle Details
        </h3>
        <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-y-4 tw-gap-x-8 tw-text-gray-700">
          <div
              v-for="(value, key) in vehicleDetails"
              :key="key"
              class="tw-flex tw-items-start tw-space-x-2"
          >
            <span class="tw-font-semibold tw-w-1/2 w-text-left">{{ key }}:</span>
            <span class="tw-text-gray-600 tw-w-1/2 tw-text-left">{{ value }}</span>
          </div>
        </div>
      </div>

      <!-- Installment Payment Schedule Section -->
      <div v-if="coverType && coverType=== 'Bima Bamba'" class="tw-bg-blue-100 tw-rounded-md tw-px-4 tw-py-6 tw-mb-6 bimabamba">
        <div class="tw-flex tw-justify-between tw-mb-2" style="color: #114cac;">
          <p class="tw-font-bold">INSTALLMENT PAYMENT SCHEDULE</p>
          <p class="tw-font-bold">12 MONTHS</p>
        </div>
        <div class="tw-flex tw-justify-between tw-mb-2" style="color: #114cac;">
          <p class="tw-font-bold">Total Premium</p>
          <p class="tw-font-bold">Kshs. {{ formatCurrency(details.totalPremium) }}</p>
        </div>
        <div v-for="(instalment, index) in details.premiumInstalments" :key="index" class="tw-flex tw-justify-between tw-py-1 tw-text-blue-600">
          <p>{{ formatDate(instalment.dueDate) }}</p>
          <p>Kshs. {{ formatCurrency(instalment.premium) }}</p>
        </div>
      </div>

      <hr class="tw-border-gray-300 tw-my-4">

      <!-- Premium Summary -->
      <div
          v-if="coverType && (coverType === 'Comprehensive' || coverType === 'Third Party')"
          :class="[
          'tw-grid tw-gap-4 tw-mb-6 tw-bg-blue-100 tw-py-2 tw-rounded-md tw-text-blue-700 tw-font-bold',
          coverType === 'Comprehensive' ? 'tw-grid-cols-2 md:tw-grid-cols-4' : 'tw-grid-cols-2 md:tw-grid-cols-3'
        ]"
      >
        <div class="tw-bg-blue-100 tw-rounded-md tw-text-blue-700 tw-font-bold tw-flex tw-flex-col tw-items-center tw-py-2">
          <p class="tw-text-center">Premium:</p>
          <span class="tw-font-normal">Kshs.{{ formatCurrency(details.premium) }}</span>
        </div>
        <div class="tw-bg-blue-100 tw-rounded-md tw-text-blue-700 tw-font-bold tw-flex tw-flex-col tw-items-center tw-py-2">
          <p class="tw-text-center">ITL & PHCF</p>
          <span class="tw-font-normal">Kshs.{{ formattedPHCFandTL }}</span>
        </div>
        <div
            v-if="coverType === 'Comprehensive'"
            class="tw-bg-blue-100 tw-rounded-md tw-text-blue-700 tw-font-bold tw-flex tw-flex-col tw-items-center tw-py-2"
        >
          <p class="tw-text-center">ADD-ONs:</p>
          <span class="tw-font-normal">Kshs.{{ formatCurrency(details.addOns) }}</span>
        </div>
        <div class="tw-bg-blue-100 tw-rounded-md tw-text-blue-700 tw-font-bold tw-flex tw-flex-col tw-items-center tw-py-2">
          <p class="tw-text-center">Stamp Duty:</p>
          <span class="tw-font-normal">Kshs.{{ formatCurrency(details.stampDuty) }}</span>
        </div>
      </div>

      <!-- Installment Payment Schedule Section for comprehensive-->
      <div v-if="coverType && coverType=== 'Comprehensive'" class="tw-bg-blue-100 tw-rounded-md tw-px-4 tw-py-6 tw-mb-6 bimabamba">
        <h3 class="tw-text-lg md:tw-text-xl tw-font-semibold tw-mb-4 tw-flex tw-justify-between" style="color: #114cac;">
          Installment
          <span v-if="details.premiumInstalments && details.premiumInstalments.length === 3">40% - 30% - 30%</span>
          <span v-if="details.premiumInstalments && details.premiumInstalments.length === 2">40% - 60%</span>
          <span v-if="details.premiumInstalments && details.premiumInstalments.length === 1">100%</span>
        </h3>
        <div class="tw-flex tw-justify-between tw-mb-2" style="color: #114cac;">
          <p class="tw-font-bold">Total Premium</p>
          <p class="tw-font-bold">Kshs. {{ formatCurrency(details.totalPremium) }}</p>
        </div>
        <div v-for="(instalment, index) in details.premiumInstalments" :key="index" class="tw-flex tw-justify-between tw-py-1 tw-text-blue-600">
          <p>{{ formatDate(instalment.dueDate) }}</p>
          <p>Kshs. {{ formatCurrency(instalment.premium) }}</p>
        </div>
      </div>

      <!-- Full Premium Section -->
      <div
          class="tw-text-white tw-rounded-md tw-flex tw-flex-col sm:tw-flex-row tw-justify-between tw-items-stretch tw-py-2 tw-text-lg md:tw-text-xl lg:tw-text-2xl tw-bg-blue-600">

        <!-- Premium Instalments -->
        <div class="tw-flex-1 tw-text-center tw-border-b sm:tw-border-r sm:tw-border-b-0 tw-border-white tw-flex tw-flex-col tw-justify-center tw-py-2 sm:tw-py-0">
          <span v-if="details.premiumInstalments.length === 3">40% - 30% - 30%</span>
          <span v-if="details.premiumInstalments.length === 2">40% - 60%</span>
          <span v-if="details.premiumInstalments.length === 1">100%</span>
          <span v-if="coverType && coverType === 'Bima Bamba'">12 Months</span>
          <span v-if="coverType && coverType === 'Third Party'">100%</span>
        </div>

        <!-- Premium Details -->
        <div class="tw-flex-1 tw-text-center tw-font-bold tw-flex tw-flex-col tw-justify-center tw-py-2 sm:tw-py-0">
          <p v-if="coverType && coverType === 'Comprehensive'">Premium Payable</p>
          <p v-if="coverType && coverType === 'Third Party'">Full Premium Amount</p>
          <p v-if="coverType && coverType === 'Bima Bamba'">Instalment Amount</p>
        </div>

        <!-- Premium to Pay -->
        <div
            v-if="coverType && (coverType === 'Bima Bamba' || coverType === 'Comprehensive') && details.premiumInstalments && details.premiumInstalments.length > 0"
            class="tw-flex-1 tw-text-center tw-border-t sm:tw-border-l sm:tw-border-t-0 tw-border-white tw-flex tw-flex-col tw-justify-center tw-py-2 sm:tw-py-0"
        >
          <p class="tw-text-sm lg:tw-text-sm">
            Premium Payable
          </p>
          <p class="tw-text-2xl md:tw-text-3xl">
            Kshs. {{ formatCurrency(details.premiumInstalments[0].premium) }}
          </p>
        </div>
        <!-- For Third Party -->
        <div
            v-if="coverType && coverType === 'Third Party'"
            class="tw-flex-1 tw-text-center tw-border-t sm:tw-border-l sm:tw-border-t-0 tw-border-white tw-flex tw-flex-col tw-justify-center tw-py-2 sm:tw-py-0"
        >
          <p class="tw-text-2xl md:tw-text-3xl">
            Kshs. {{ formatCurrency(details.totalPremium) }}
          </p>
        </div>
      </div>
      <div class="tw-flex tw-justify-center tw-mt-5 tw-pb-8">
        <v-btn style="background-color: #114cac; " block large color="primary" class="custom-btn my-3  tw-font-extrabold tw-text-lg md:tw-text-2xl" @click="proceedToPayment">
          Proceed to Payment
        </v-btn>
      </div>
    </div>
  </div>
</template>
<script>
import axios from 'axios';
import { log } from '@/utils/errorLogger';

export default {
  name: 'VerifyDetails, VehiclePhotoUpload',
  data() {
    return {
      details: {},
    };
  },
  computed: {
    coverType() {
      return this.details.coverType || 'N/A';
    },
    policyName() {
      const savedPolicyTypes = JSON.parse(localStorage.getItem('savedPolicyTypes'));
      return savedPolicyTypes?.selectedPolicy?.name?.trim() || null;
    },
    formattedQuotePeriod() {
      const startDate = this.formatDate(this.details.startDate);
      const renewalDate = this.formatDate(this.details.renewalDate);
      if (!startDate || !renewalDate || startDate === 'N/A' || renewalDate === 'N/A') {
        return 'N/A';
      }
      return `${startDate} - ${renewalDate}`;
    },
    quoteNumber() {
      return this.details.quoteNumber || 'N/A';
    },
    principalDetails() {
      const formPayload = JSON.parse(localStorage.getItem('formPayload')) || {};
      const customer = this.details.customer || {};
      const isCorporate = formPayload.isCorporate;

      if (isCorporate === false) {
        // Individual details (non-corporate)
        return {
          'First Name': customer.firstName || formPayload.firstName || '',
          'Full Name': formPayload.fullname || '',
          idNumber: formPayload.idNumber || '',
          kra: formPayload.kra || '',
          Gender: customer.gender || formPayload.gender || '',
          DOB: formPayload.dob ? this.formatDate(formPayload.dob) : null,
          'Phone No.': customer.mobileNo || formPayload.mobileNo || '',
          Email: customer.email || formPayload.email || '',
        };
      }
      if (isCorporate === true) {
        // Corporate details
        return {
          Name: formPayload.companyName || '',
          Registration: formPayload.registrationNumber || '',
          'Date of Registration': formPayload.dateofRegistration ? this.formatDate(formPayload.dateofRegistration) : null,
          Email: formPayload.email || '',
          Phone: formPayload.mobileNo || '',
          'KRA Pin': formPayload.kraPin || '',
        };
      }
      return {};
    },

    vehicleDetails() {
      const vehicle = this.details.vehicle || {};
      return {
        Make: vehicle.make || 'N/A',
        'Registration No': vehicle.regNo || 'N/A',
        Model: vehicle.model || 'N/A',
        Year: vehicle.yearOfManufacture || 'N/A',
        Value: this.formatCurrency(vehicle.value),
        Color: vehicle.vehicleColor || 'N/A',
        'Engine No': vehicle.engineNo || 'N/A',
        'Chassis No': vehicle.chassisNumber || 'N/A',
        'Body Type': vehicle.bodyType || 'N/A',
        Cc: vehicle.cc || 'N/A',
      };
    },
    getFullName() {
      const { firstName, middleName, lastName } = this.details.customer || {};
      return [firstName, middleName, lastName].filter(Boolean).join(' ');
    },
    formattedPHCFandTL() {
      const phcf = Number(this.details.phcf || 0);
      const tl = Number(this.details.tl || 0);
      const sum = phcf + tl;
      return this.formatCurrency(sum);
    },
  },
  methods: {
    // scrollToUploadSection() {
    //   this.$nextTick(() => {
    //     if (this.isChecked) {
    //       const uploadSection = document.querySelector('.photo-upload-grid');
    //       if (uploadSection) {
    //         uploadSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    //       }
    //     } else {
    //       const headerSection = document.querySelector('.vehicle-upload-section');
    //       if (headerSection) {
    //         headerSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    //       }
    //     }
    //   });
    // },
    // handleUploadClick(photoType, index) {
    //   this.$refs[`fileInput_${index}`][0].click();
    // },
    // async uploadFile(file, photoType) {
    //   try {
    //     const storedData = JSON.parse(localStorage.getItem('formPayload'));
    //     const formData = new FormData();
    //     formData.append('file', file);
    //     formData.append('fileName', photoType);
    //     formData.append('customerId', storedData.customerId);
    //     formData.append('quoteId', storedData.quoteId);
    //     await axios.post('/motor/api/quote/document/upload', formData, {
    //       headers: {
    //         'Content-Type': 'multipart/form-data',
    //       },
    //       onUploadProgress: (progressEvent) => {
    //         this.uploadProgress[photoType] = Math.round((progressEvent.loaded * 100) / progressEvent.total);
    //       },
    //     });
    //     this.$toast.success(`${photoType} uploaded successfully`);
    //   } catch (error) {
    //     this.$toast.error(`Error uploading ${photoType}`);
    //     // Handle error
    //   } finally {
    //     delete this.uploadProgress[photoType];
    //   }
    // },
    async fetchDetails() {
      const quoteId = localStorage.getItem('quoteId') || null;
      try {
        const response = await axios.post('/motor/api/quote/portal/detail', { quoteId });
        this.details = response.data.data || {};
      } catch (error) {
        log(error, this, 'fetchDetails');
        this.$toast.error('Error getting details...');
      }
    },
    formatDate(timestamp) {
      if (!timestamp) return 'N/A';
      const date = new Date(timestamp);
      return date.toLocaleDateString('en-GB');
    },
    formatCurrency(amount) {
      if (!amount) return '0';
      return Number(amount).toLocaleString();
    },
    proceedToPayment() {
      this.$emit('next');
    },
  },
  async mounted() {
    await this.fetchDetails();
  },
};
</script>

<style scoped>
.verify-details {
  padding-top: 2rem;
  padding-bottom: 2rem;
}
.custom-btn {
  height: auto;
  padding-top: 0.75rem; /* py-3 in tw classes */
  padding-bottom: 0.75rem; /* py-3 in tw classes */
  padding-left: 1.5rem; /* px-6 in tw classes */
  padding-right: 1.5rem; /* px-6 in tw classes */
  font-size: 1rem; /* text-lg in tw classes for smaller screens */
}
@media (min-width: 768px) { /* md: */
  .custom-btn {
    font-size: 1.5rem; /* text-2xl for medium and larger screens */
    height: 60px;
    padding-left: 24px; /* Custom px value for larger screens */
    padding-right: 24px; /* Custom px value for larger screens */
  }
}

.upload-box:hover {
  border-color: #2563eb;
}
</style>
